import {
  ArrayField,
  Datagrid,
  DateField,
  FunctionField,
  ReferenceField,
  SimpleShowLayout,
  TextField,
  useGetList,
  useRecordContext,
} from "react-admin";
import { settings } from "app";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import permissions from "app/permissions";
import { CandidacyStates, DebtStatuses, PaymentProvider, TransactionStatuses } from "utils/constants";
import TransferCandidacyPaymentsDialog from "./TransferCandidacyPaymentsDialog";
import { Box, CircularProgress, Grid, Typography, useMediaQuery } from "@mui/material";
import SecuredComponent from "server/security/SecuredComponent";
import { paymentRequests } from "api/paymentApi";
import { debtsRequests } from "api/debtsApi";
import ApproveCashPaymentModal from "./ApproveCashPaymentModal";
import { SectionTitle } from "features/SectionTitle";

const DebtTransactionsExpanded = ({ isForeigner, disabled }) => {
  const record = useRecordContext();

  const { data, isLoading } = useGetList(
    `debt/debts/${record?.debtId}/transactions`,
    {},
    { enabled: !!record?.debtId },
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        my={2}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!data?.length) {
    return (
      <Box
        my={2}
        mx={2}
      >
        <Typography
          variant="body2"
          color="text.secondary"
        >
          Няма транзакции
        </Typography>
      </Box>
    );
  }

  return (
    <Datagrid
      data={data}
      rowClick={false}
      bulkActionButtons={false}
      sx={settings.style.secondaryDatagrid}
    >
      <TextField
        label="ID"
        source="id"
        emptyText="-"
      />
      <ReferenceField
        sortable={false}
        label="Канал"
        source="providerId"
        reference={paymentRequests.getPaymentProvidersSelect()}
      >
        <TextField
          label="Име"
          source="name"
          emptyText="-"
        />
      </ReferenceField>

      <FunctionField
        label="Сума"
        render={rec => `${rec.amount.toFixed(2)} ${rec.currency}`}
      />

      <FunctionField
        label="Статус"
        render={rec => TransactionStatuses[rec.status] || rec.status}
      />

      <DateField
        label="Платено на"
        source="paymentTimestamp"
        locales="bg-BG"
        showTime
        emptyText="-"
      />

      <TextField
        label="Описание"
        source="description"
        emptyText="-"
      />

      {isForeigner && checkPermission(getPermissions(), permissions.canPayTransactions) ? (
        <FunctionField
          label=""
          render={rec =>
            rec.status === "Processing" && rec.providerId === PaymentProvider.CashPayment ? (
              <ApproveCashPaymentModal
                transaction={rec}
                disabled={disabled}
              />
            ) : null
          }
        />
      ) : null}
    </Datagrid>
  );
};

const CandidacyPayments = ({ candidacyId, candidacyStateId, isForeigner, usingPreparatoryClass, disabled }) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  return (
    <SimpleShowLayout>
      <SectionTitle
        title="Плащания"
        textAlign="center"
        fontSize={18}
      />
      <Grid
        container
        spacing={3}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <ArrayField source="debts">
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
              sx={settings.style.secondaryDatagrid}
              expand={
                <DebtTransactionsExpanded
                  isForeigner={isForeigner}
                  disabled={disabled}
                />
              }
              isRowExpandable={rec => !!rec.debtId}
              expandSingle
            >
              <TextField
                label="ID"
                source="debtId"
                emptyText="-"
              />
              <ReferenceField
                sortable={false}
                label="Вид"
                source="debtTypeId"
                reference={debtsRequests.getDebtTypesSelect()}
              >
                <TextField
                  source="nameBg"
                  emptyText="-"
                />
              </ReferenceField>
              <TextField
                label="Сума"
                source="amount"
                emptyText="-"
                noWrap
              />

              <FunctionField
                label="Статус"
                render={rec => DebtStatuses[rec.status] || rec.status}
              />

              <TextField
                label="Създадено на"
                source="createTimestampFormatted"
                emptyText="-"
              />

              <TextField
                label="Платено на"
                source="paymentTimestampFormatted"
                emptyText="-"
              />
            </Datagrid>
          </ArrayField>
          <Grid sx={{ display: "flex", justifyContent: "flex-end" }}>
            {candidacyStateId === CandidacyStates.Approved && !usingPreparatoryClass && (
              <SecuredComponent permission={permissions.canTransferCandidacyPayments}>
                <TransferCandidacyPaymentsDialog
                  candidacyId={candidacyId}
                  disabled={disabled}
                />
              </SecuredComponent>
            )}
          </Grid>
        </Grid>
      </Grid>
    </SimpleShowLayout>
  );
};

export default CandidacyPayments;
